import * as React from "react";
import { ToggleContainerModel, ToggleContainerProps } from "./typings";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "bernie-view";
import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps,
} from "components/flexFramework/domain/BlossomComponentFactory";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";
import { UitkExpandoCard } from "uitk-react-expando";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading } from "uitk-react-text";
import { getExtraClassNameBasedOnParameters } from "../SimpleContainer/utilityStyles";

export interface ToggleContainerContext {
  modulesHaveBorder?: boolean;
}

const defaultContext = {
  modulesHaveBorder: false,
};

export const ToggleContainerContext = React.createContext<ToggleContainerContext>(defaultContext);

const DEFAULT_WIDTH = "100";

export const ToggleContainer = (props: ToggleContainerProps) => {
  const blossomComponent = BlossomComponentFactory.get(props as BlossomComponentFactoryProps);
  const context = props.context;

  if (!blossomComponent) {
    return null;
  }

  const model = blossomComponent.model as ToggleContainerModel;
  const { name, mobileWidth, title, width, innerTitle, tabletWidth, desktopWidth, collapsed } = model;
  const modules = blossomComponent.getBlossomComponents();
  const modulesHaveBorder = model.hasBorder;

  // Return null for regions with no title, inner title and all "empty" modules
  if (
    (title &&
      innerTitle &&
      modules &&
      modules.length > 0 &&
      modules.filter((module) => module.shouldRender()).length === 0) ||
    !modules.length
  ) {
    return null;
  }

  const gridWidth = regionWidthToGridMap[width || DEFAULT_WIDTH];
  const gridTabletWidth = regionWidthToGridMap[tabletWidth || DEFAULT_WIDTH];
  const gridMobileWidth = regionWidthToGridMap[mobileWidth || DEFAULT_WIDTH];
  const commonClassNames = ["uitk-grid uitk-cell", getExtraClassNameBasedOnParameters(model)]
    .filter((classes) => classes !== "")
    .join(" ");

  const ContainerContent = (containerProps: { includeTitle: boolean; classNames: string }) => (
    <div className={` ${commonClassNames} ${containerProps.classNames} `} role="region">
      {title && (
        <UitkSpacing>
          <UitkHeading
            level={2}
            size={700}
            className={`${!containerProps.includeTitle && "is-visually-hidden"} uitk-cell all-cell-1-1`}
          >
            {title}
          </UitkHeading>
        </UitkSpacing>
      )}
      {innerTitle && (
        <UitkSpacing padding={{ inlinestart: "three" }}>
          <UitkHeading level={3} size={700} className="uitk-cell all-cell-1-1">
            {innerTitle}
          </UitkHeading>
        </UitkSpacing>
      )}
      <BlossomComponents blossomComponent={blossomComponent} context={context} />
    </div>
  );
  const [isVisible, setIsVisible] = React.useState(collapsed ? false : true);
  const toggle = React.useCallback(() => setIsVisible(!isVisible), [isVisible]);

  return (
    <ToggleContainerContext.Provider value={{ modulesHaveBorder }}>
      <Viewport>
        {mobileWidth === "hidden" ? (
          <ViewSmall />
        ) : (
          <ViewSmall>
            <UitkSpacing padding={{ block: "three", inline: "three" }}>
              <div className="SimpleContainer ToggleContainer">
                <UitkExpandoCard
                  isVisible={isVisible}
                  handleClick={toggle}
                  expandoTitle={title}
                  expandoDescription={
                    <ContainerContent
                      classNames={`SimpleContainer ${
                        name === "dx-page-header" ? " elevation-4" : ""
                      } ${gridMobileWidth}`}
                      includeTitle={false}
                    />
                  }
                />
              </div>
            </UitkSpacing>
          </ViewSmall>
        )}

        {tabletWidth === "hidden" ? (
          <ViewMedium />
        ) : (
          <ViewMedium>
            <UitkSpacing padding={{ block: "three", inline: "three" }}>
              <div className="SimpleContainer ToggleContainer">
                <UitkExpandoCard
                  isVisible={isVisible}
                  handleClick={toggle}
                  expandoTitle={title}
                  expandoDescription={
                    <ContainerContent
                      classNames={`SimpleContainer ${
                        name === "dx-page-header" ? " elevation-4" : ""
                      } ${gridTabletWidth}`}
                      includeTitle={false}
                    />
                  }
                />
              </div>
            </UitkSpacing>
          </ViewMedium>
        )}

        {desktopWidth === "hidden" ? (
          <ViewLarge />
        ) : (
          <ViewLarge>
            <UitkSpacing padding={{ block: "three", inline: "three" }}>
              <div className="SimpleContainer ToggleContainer">
                <UitkExpandoCard
                  isVisible={isVisible}
                  handleClick={toggle}
                  expandoTitle={title}
                  expandoDescription={
                    <ContainerContent
                      classNames={` ${name === "dx-page-header" ? " elevation-4" : "SimpleContainer"} ${gridWidth}`}
                      includeTitle={false}
                    />
                  }
                />
              </div>
            </UitkSpacing>
          </ViewLarge>
        )}
      </Viewport>
    </ToggleContainerContext.Provider>
  );
};

/**
 * Return the classes corresponding to a specific region. This is
 * a temporary solution until Module groups are implemented
 * @param {string} regionName - Name of region
 * @return {string} classes to decorate region
 */
/* istanbul ignore next */

const regionWidthToGridMap = {
  "25": "all-cell-1-4",
  "33": "all-cell-1-3",
  "50": "all-cell-1-2",
  "66": "all-cell-2-3",
  "75": "all-cell-3-4",
  "100": "all-cell-1-1",
};

export default ToggleContainer;
